import type { AgentEvent } from "@bigpp/trpc";
import { useCallback, useEffect, useRef, useState } from "react";

import { log } from "../log.ts";
import { runInSandbox } from "../bridge/controller.ts";
import { executeOps } from "../bridge/office-ops.ts";
import { type BridgeResult } from "../bridge/protocol.ts";
import { detectHostCapabilities, type HostCapabilities } from "../office-ready.ts";
import { getDeckActiveThreadId, setDeckActiveThreadId } from "../deck-thread.ts";
import { captureDeckBytes, listSnapshots, saveSnapshot, bytesToBase64 } from "../snapshots.ts";
import { parseDeck } from "../pptx.ts";
import { queryClient, trpc, trpcClient } from "../trpc.ts";
import {
  applySlideMappings,
  insertAllSlidesAsScratch,
  type AppliedSlideMapping,
} from "./applySlideMappings.ts";

const stripPeekSlideImages = (toolCalls: ToolCallCard[]): ToolCallCard[] =>
  toolCalls.map((tc) => {
    if (tc.toolName !== "peek_slide") return tc;
    return {
      toolName: "peek_slide" as const,
      toolCallId: tc.toolCallId,
      slideIndex: tc.slideIndex,
      status: tc.status,
      ...(tc.error !== undefined ? { error: tc.error } : {}),
    };
  });

const finishRunningToolCalls = (
  toolCalls: ToolCallCard[],
  error = "Interrupted",
): ToolCallCard[] =>
  toolCalls.map((tc) =>
    tc.status === "running" ? { ...tc, status: "error" as const, error } : tc,
  );

export type AttachmentInput = {
  id: string;
  file: File;
  filename: string;
  mediaType: string;
};

export type PlanItemStatus = "pending" | "in_progress" | "completed";

export type ToolCallCard =
  | {
      toolName: "execute_office_js_code";
      toolCallId: string;
      code: string;
      status: "running" | "ok" | "error";
      result?: BridgeResult;
      error?: string;
    }
  | {
      toolName: "upload_current_deck_to_sandbox";
      toolCallId: string;
      status: "running" | "ok" | "error";
      result?: { slideCount: number; path?: string };
      error?: string;
    }
  | {
      toolName: "insert_deck_slides";
      toolCallId: string;
      attachmentId: string;
      status: "running" | "ok" | "error";
      result?: {
        firstIndex?: number;
        count?: number;
        scratchTag?: string;
        applied?: AppliedSlideMapping[];
      };
      error?: string;
    }
  | {
      toolName: "peek_slide";
      toolCallId: string;
      slideIndex: number;
      status: "running" | "ok" | "error";
      base64?: string;
      mediaType?: string;
      error?: string;
    }
  | {
      toolName: "use_skill";
      toolCallId: string;
      slug: string;
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "ask_user";
      toolCallId: string;
      question: string;
      options?: string[];
      // "running" until the user replies. Once replied, status flips to "ok"
      // and `answer` is populated so the card renders read-only.
      status: "running" | "ok" | "error";
      answer?: string;
    }
  | {
      toolName: "create_skill";
      toolCallId: string;
      slug: string;
      name: string;
      description: string;
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "edit_skill";
      toolCallId: string;
      slug: string;
      name: string;
      description: string;
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "delete_skill";
      toolCallId: string;
      slug: string;
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "list_skills";
      toolCallId: string;
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "insert_attachment_image";
      toolCallId: string;
      attachmentId: string;
      slideIndex: number;
      status: "running" | "ok" | "error";
      result?: { slideIndex: number; shapeId: string };
      error?: string;
    }
  | {
      toolName: "insert_sandbox_image";
      toolCallId: string;
      filename: string;
      slideIndex: number;
      status: "running" | "ok" | "error";
      result?: { slideIndex: number; shapeId: string };
      error?: string;
    }
  | {
      toolName: "insert_sandbox_deck_slides";
      toolCallId: string;
      filename: string;
      status: "running" | "ok" | "error";
      result?: {
        firstIndex?: number;
        count?: number;
        scratchTag?: string;
        applied?: AppliedSlideMapping[];
      };
      error?: string;
    }
  | {
      toolName: "code_interpreter";
      toolCallId: string;
      code?: string;
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "file_search";
      toolCallId: string;
      queries?: string[];
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "web_search";
      toolCallId: string;
      queries?: string[];
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "create_plan";
      toolCallId: string;
      title?: string;
      items: { text: string; status: PlanItemStatus }[];
      status: "running" | "ok" | "error";
    }
  | {
      toolName: "jsx_preview";
      toolCallId: string;
      title?: string;
      html: string;
      status: "running" | "ok" | "error";
    };

// Ordered progress parts shown inside the collapsible "Thinking" panel above
// the assistant's final reply. Reasoning chunks and tool steps interleave in
// the order they were emitted, so the user can follow the model's flow.
export type ProgressPart =
  | { kind: "reasoning"; id: string; text: string }
  | { kind: "tool"; toolCallId: string };

export type ChatMsg =
  | {
      id: string;
      role: "user";
      content: string;
      attachments: { filename: string }[];
      // Set once the pre-run deck snapshot has been written to IndexedDB.
      // null while in flight or on capture failure.
      snapshotAt: number | null;
    }
  | {
      id: string;
      role: "assistant";
      content: string;
      progressParts: ProgressPart[];
      toolCalls: ToolCallCard[];
      sources: { sourceId: string; url: string; title?: string }[];
      followUps: string[];
      startedAt: number;
      finishedAt: number | null;
      finished: boolean;
    };

export type AgentRunStatus =
  | { kind: "idle" }
  | { kind: "running"; runId: string }
  | { kind: "error"; message: string };

const newId = (): string => globalThis.crypto.randomUUID();

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buf = await blob.arrayBuffer();
  // Chunked to avoid blowing the stack on large files.
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return globalThis.btoa(binary);
};

export const useAgentRun = (): {
  messages: ChatMsg[];
  status: AgentRunStatus;
  send: (text: string, attachments: AttachmentInput[]) => Promise<void>;
  stop: () => void;
  newChat: () => void;
  answerAskUser: (toolCallId: string, answer: string) => void;
  pinnedSkillIds: string[];
  setPinnedSkillIds: (ids: string[]) => void;
  activeThreadId: string | null;
  loadThread: (id: string) => Promise<void>;
} => {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const messagesRef = useRef<ChatMsg[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [status, setStatus] = useState<AgentRunStatus>({ kind: "idle" });
  const [pinnedSkillIds, setPinnedSkillIdsState] = useState<string[]>(() => {
    try {
      const raw = globalThis.localStorage?.getItem("bigpp.pinnedSkillIds");
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
    } catch {
      return [];
    }
  });
  const setPinnedSkillIds = useCallback((ids: string[]): void => {
    setPinnedSkillIdsState(ids);
    try {
      globalThis.localStorage?.setItem("bigpp.pinnedSkillIds", JSON.stringify(ids));
    } catch {
      // best-effort
    }
  }, []);

  const start = trpc.runs.start.useMutation();
  const cancel = trpc.runs.cancel.useMutation();
  const sendToolResult = trpc.runs.toolResult.useMutation();
  const upload = trpc.attachments.upload.useMutation();
  const appendThreadMessage = trpc.threads.append.useMutation();

  // Currently-open thread. Null means "will be created on next user send".
  // Association is stored on the open presentation via Office document settings.
  const activeThreadIdRef = useRef<string | null>(null);
  const [activeThreadId, setActiveThreadIdState] = useState<string | null>(null);
  const setActiveThreadId = useCallback((id: string | null): void => {
    activeThreadIdRef.current = id;
    setActiveThreadIdState(id);
    void setDeckActiveThreadId(id);
  }, []);

  const activeRunIdRef = useRef<string | null>(null);
  const activeAssistantIdRef = useRef<string | null>(null);
  const bridgeAbortRef = useRef<AbortController | null>(null);
  const subUnsubRef = useRef<(() => void) | null>(null);
  // PowerPointApi capabilities (varies a lot across Windows/Mac/web/older
  // builds). Detected once when Office.js is ready and sent with every
  // runs.start so the model can pick a path that works.
  const hostCapabilitiesRef = useRef<HostCapabilities | null>(null);
  useEffect(() => {
    void detectHostCapabilities().then((caps) => {
      hostCapabilitiesRef.current = caps;
    });
  }, []);
  // Sticky list of server attachment ids the agent has access to this
  // session. Each id is the DB row id returned by attachments.upload.
  // Files only live on the OpenAI side once uploaded — there's no local
  // byte cache here anymore.
  const sessionAttachmentIdsRef = useRef<string[]>([]);
  // Maps client-side ephemeral ids (used by the Composer's pending list) to
  // their post-upload server attachment id, so we never re-upload the same
  // File within a session.
  const clientToServerIdRef = useRef<Map<string, string>>(new Map());

  const stop = useCallback((): void => {
    const runId = activeRunIdRef.current;
    const assistantId = activeAssistantIdRef.current;
    if (runId) {
      cancel.mutate({ runId });
    }
    bridgeAbortRef.current?.abort();
    subUnsubRef.current?.();
    subUnsubRef.current = null;
    activeRunIdRef.current = null;
    activeAssistantIdRef.current = null;
    // Don't wait for the server `cancelled` event — unsubscribing drops it.
    if (assistantId) {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantId || m.role !== "assistant" || m.finished) return m;
          return {
            ...m,
            content: m.content + (m.content.length > 0 ? "\n\n" : "") + "[cancelled]",
            finished: true,
            finishedAt: Date.now(),
            toolCalls: finishRunningToolCalls(m.toolCalls, "Cancelled"),
          };
        }),
      );
    }
    setStatus({ kind: "idle" });
  }, [cancel]);

  const newChat = useCallback((): void => {
    const runId = activeRunIdRef.current;
    if (runId) cancel.mutate({ runId });
    bridgeAbortRef.current?.abort();
    subUnsubRef.current?.();
    subUnsubRef.current = null;
    activeRunIdRef.current = null;
    activeAssistantIdRef.current = null;
    setMessages([]);
    setStatus({ kind: "idle" });
    setActiveThreadId(null);
  }, [cancel, setActiveThreadId]);

  // Rehydrate a thread's messages from the server. Cancels any in-flight run
  // first so we can't cross-contaminate state.
  const loadThread = useCallback(
    async (id: string): Promise<void> => {
      const runId = activeRunIdRef.current;
      if (runId) cancel.mutate({ runId });
      bridgeAbortRef.current?.abort();
      subUnsubRef.current?.();
      subUnsubRef.current = null;
      activeRunIdRef.current = null;
      activeAssistantIdRef.current = null;

      const res = await trpcClient.threads.get.query({ id });
      // Snapshots live in this device's IndexedDB. When rehydrating a thread,
      // check which user messages still have a local snapshot so the Revert
      // button reappears — otherwise reopening a past thread would look like
      // history was frozen. On a fresh device the map is empty; that's fine.
      const snapshotByMsgId = new Map<string, number>();
      try {
        for (const s of await listSnapshots()) {
          snapshotByMsgId.set(s.messageId, s.capturedAt);
        }
      } catch {
        // best-effort — no revert UI if snapshots can't be read
      }
      const restored: ChatMsg[] = res.messages.map((m) => {
        // payloadJson is unknown; pull only the fields we know how to render.
        const p =
          m.payloadJson && typeof m.payloadJson === "object"
            ? (m.payloadJson as Record<string, unknown>)
            : {};
        if (m.role === "user") {
          const attachments =
            Array.isArray(p["attachments"])
              ? (p["attachments"] as { filename?: string }[]).flatMap((a) =>
                  typeof a?.filename === "string" ? [{ filename: a.filename }] : [],
                )
              : [];
          return {
            id: m.id,
            role: "user" as const,
            content: m.content,
            attachments,
            snapshotAt: snapshotByMsgId.get(m.id) ?? null,
          };
        }
        const toolCalls = stripPeekSlideImages(
          Array.isArray(p["toolCalls"]) ? (p["toolCalls"] as ToolCallCard[]) : [],
        );
        const sources = Array.isArray(p["sources"])
          ? (p["sources"] as { sourceId: string; url: string; title?: string }[])
          : [];
        const followUps = Array.isArray(p["followUps"])
          ? (p["followUps"] as string[])
          : [];
        const progressParts = Array.isArray(p["progressParts"])
          ? (p["progressParts"] as ProgressPart[])
          : [];
        return {
          id: m.id,
          role: "assistant" as const,
          content: m.content,
          progressParts,
          toolCalls,
          sources,
          followUps,
          startedAt: new Date(m.createdAt).getTime(),
          finishedAt: new Date(m.createdAt).getTime(),
          finished: true,
        };
      });
      setMessages(restored);
      setStatus({ kind: "idle" });
      setActiveThreadId(id);
    },
    [cancel, setActiveThreadId],
  );

  // Restore only the thread last open for this presentation (Office document
  // settings). No deck association → empty pane; never fall back to another
  // deck's thread.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const target = await getDeckActiveThreadId();
        if (cancelled || !target) return;
        await loadThread(target);
      } catch {
        if (!cancelled) void setDeckActiveThreadId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once at mount; loadThread is stable enough (only depends on cancel).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answerAskUser = useCallback(
    (toolCallId: string, answer: string): void => {
      const runId = activeRunIdRef.current;
      if (!runId) return;
      // Ship the answer to the server so the paused tool.execute() promise
      // resolves and the run continues. Then flip the local card to "ok" so
      // the UI shows the user's own reply.
      sendToolResult.mutate({ runId, toolCallId, result: { answer } });
      setMessages((prev) =>
        prev.map((m) => {
          if (m.role !== "assistant") return m;
          return {
            ...m,
            toolCalls: m.toolCalls.map((tc) =>
              tc.toolCallId === toolCallId && tc.toolName === "ask_user"
                ? { ...tc, status: "ok", answer }
                : tc,
            ),
          };
        }),
      );
    },
    [sendToolResult],
  );

  const updateAssistant = useCallback(
    (
      assistantId: string,
      mut: (
        prev: Extract<ChatMsg, { role: "assistant" }>,
      ) => Extract<ChatMsg, { role: "assistant" }>,
    ): void => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== assistantId) return m;
          if (m.role !== "assistant") return m;
          const next = mut(m);
          // Ensure each toolCall has a corresponding tool progress part.
          // This keeps the chronological "Thinking" panel in sync without
          // every tool branch having to remember to push a progress part.
          const known = new Set(
            next.progressParts.flatMap((p) => (p.kind === "tool" ? [p.toolCallId] : [])),
          );
          const additions: ProgressPart[] = next.toolCalls
            .filter((tc) => !known.has(tc.toolCallId))
            .map((tc) => ({ kind: "tool" as const, toolCallId: tc.toolCallId }));
          if (additions.length === 0) return next;
          return { ...next, progressParts: [...next.progressParts, ...additions] };
        }),
      );
    },
    [],
  );

  const send = useCallback(
    async (text: string, attachments: AttachmentInput[]): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed && attachments.length === 0) return;
      if (status.kind === "running") return;

      const userMsg: ChatMsg = {
        id: newId(),
        role: "user",
        content: trimmed,
        attachments: attachments.map((a) => ({ filename: a.filename })),
        snapshotAt: null,
      };
      const assistantId = newId();
      const assistantMsg: ChatMsg = {
        id: assistantId,
        role: "assistant",
        content: "",
        progressParts: [],
        toolCalls: [],
        sources: [],
        followUps: [],
        startedAt: Date.now(),
        finishedAt: null,
        finished: false,
      };

      // Snapshot history *before* the new user message.
      const historyForServer: Array<{ role: "user" | "assistant"; content: string }> = [];
      for (const m of messages) {
        if (m.role === "user") {
          historyForServer.push({ role: "user", content: m.content });
        } else if (m.finished && m.content.length > 0) {
          historyForServer.push({ role: "assistant", content: m.content });
        }
      }

      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      log.info("addin.agent.send", {
        threadId: activeThreadIdRef.current,
        charCount: trimmed.length,
      });

      // Persist the user message to the thread. If no thread exists yet, the
      // server creates one; either way we stash the id so the assistant reply
      // can land in the same thread. Non-blocking — failure just skips
      // persistence for this turn.
      void appendThreadMessage
        .mutateAsync({
          threadId: activeThreadIdRef.current,
          role: "user",
          content: trimmed,
          payload: {
            attachments: userMsg.attachments,
          },
        })
        .then((res) => {
          if (activeThreadIdRef.current !== res.threadId) {
            setActiveThreadId(res.threadId);
          }
        })
        .catch(() => {
          // best-effort
        });

      // Capture a pre-run deck snapshot, keyed by this user message's id, so
      // the user can revert to "deck state before I sent this message". The
      // agent may mutate the deck during the run; we snapshot first.
      // Failures are non-fatal — the run proceeds, the revert button just
      // never appears for this message.
      try {
        const bytes = await captureDeckBytes();
        const capturedAt = Date.now();
        const { evictedIds } = await saveSnapshot(userMsg.id, bytes, capturedAt);
        const evictedSet = new Set(evictedIds);
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id === userMsg.id && m.role === "user") {
              return { ...m, snapshotAt: capturedAt };
            }
            if (m.role === "user" && evictedSet.has(m.id)) {
              return { ...m, snapshotAt: null };
            }
            return m;
          }),
        );
      } catch {
        // Best-effort: keep going without a revert point for this message.
      }

      // Upload each new attachment to OpenAI Files (via our server) before
      // kicking off the run, so it's reachable as an `input_file` content
      // part on the first model call. Re-uses prior uploads in the same
      // session via clientToServerIdRef.
      try {
        for (const a of attachments) {
          if (clientToServerIdRef.current.has(a.id)) continue;
          const base64 = await blobToBase64(a.file);
          const res = await upload.mutateAsync({
            filename: a.filename,
            mediaType: a.mediaType,
            base64,
          });
          clientToServerIdRef.current.set(a.id, res.id);
          sessionAttachmentIdsRef.current.push(res.id);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({ kind: "error", message });
        updateAssistant(assistantId, (m) => ({
          ...m,
          content: `Upload failed: ${message}`,
          finished: true,
          finishedAt: Date.now(),
        }));
        return;
      }

      let runId: string;
      try {
        const res = await start.mutateAsync({
          message: trimmed || "(no message)",
          history: historyForServer,
          attachmentIds: [...sessionAttachmentIdsRef.current],
          pinnedSkillIds,
          hostCapabilities: hostCapabilitiesRef.current ?? undefined,
        });
        runId = res.runId;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({ kind: "error", message });
        updateAssistant(assistantId, (m) => ({
          ...m,
          content: `Error: ${message}`,
          finished: true,
          finishedAt: Date.now(),
        }));
        return;
      }

      activeRunIdRef.current = runId;
      activeAssistantIdRef.current = assistantId;
      const bridgeAbort = new AbortController();
      bridgeAbortRef.current = bridgeAbort;
      setStatus({ kind: "running", runId });

      const replyOk = (toolCallId: string, result: unknown): void => {
        sendToolResult.mutate({ runId, toolCallId, result });
      };
      const replyErr = (toolCallId: string, message: string): void => {
        sendToolResult.mutate({ runId, toolCallId, error: message });
      };

      // Snapshot the assistant message from state and persist it to the
      // active thread. Called at run-finish / cancelled / error. Fire-and-forget.
      const persistAssistant = (id: string): void => {
        const msg = messagesRef.current.find((m) => m.id === id);
        if (!msg || msg.role !== "assistant") return;
        const threadId = activeThreadIdRef.current;
        if (!threadId) return; // no user turn was persisted; skip
        void appendThreadMessage
          .mutateAsync({
            threadId,
            role: "assistant",
            content: msg.content,
            payload: {
              toolCalls: stripPeekSlideImages(msg.toolCalls),
              sources: msg.sources,
              followUps: msg.followUps,
              progressParts: msg.progressParts,
            },
          })
          .catch(() => {
            // best-effort
          });
      };

      const handleEvent = (evt: AgentEvent): void => {
        switch (evt.type) {
          case "text-delta":
            updateAssistant(assistantId, (m) => ({ ...m, content: m.content + evt.delta }));
            return;
          case "follow-ups":
            updateAssistant(assistantId, (m) => ({ ...m, followUps: evt.suggestions }));
            return;
          case "source-url":
            updateAssistant(assistantId, (m) => {
              if (m.sources.some((s) => s.sourceId === evt.sourceId)) return m;
              return {
                ...m,
                sources: [
                  ...m.sources,
                  {
                    sourceId: evt.sourceId,
                    url: evt.url,
                    ...(evt.title !== undefined ? { title: evt.title } : {}),
                  },
                ],
              };
            });
            return;
          case "reasoning-delta":
            updateAssistant(assistantId, (m) => {
              const last = m.progressParts[m.progressParts.length - 1];
              if (last && last.kind === "reasoning") {
                const updated: ProgressPart = {
                  kind: "reasoning",
                  id: last.id,
                  text: last.text + evt.delta,
                };
                return {
                  ...m,
                  progressParts: [...m.progressParts.slice(0, -1), updated],
                };
              }
              return {
                ...m,
                progressParts: [
                  ...m.progressParts,
                  { kind: "reasoning", id: newId(), text: evt.delta },
                ],
              };
            });
            return;
          case "step-start":
            // No state mutation — kept as an event so we could add a UI ping
            // later. Right now the new reasoning/tool cards are enough signal.
            return;
          case "tool-call-request": {
            if (evt.toolName === "create_plan") {
              const { title, items } = evt.args;
              const planItems = items.map((text) => ({
                text,
                status: "pending" as PlanItemStatus,
              }));
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "create_plan",
                    toolCallId: evt.toolCallId,
                    title,
                    items: planItems,
                    status: "ok",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "update_plan") {
              const updates = evt.args.updates;
              // Patch the most recent create_plan card; intentionally do not
              // add a new card for the update event itself.
              updateAssistant(assistantId, (m) => {
                const lastPlanIdx = (() => {
                  for (let i = m.toolCalls.length - 1; i >= 0; i--) {
                    if (m.toolCalls[i]?.toolName === "create_plan") return i;
                  }
                  return -1;
                })();
                if (lastPlanIdx < 0) return m;
                const plan = m.toolCalls[lastPlanIdx];
                if (!plan || plan.toolName !== "create_plan") return m;
                const nextItems = plan.items.map((it, i) => {
                  const u = updates.find((u) => u.index === i);
                  return u ? { ...it, status: u.status } : it;
                });
                return {
                  ...m,
                  toolCalls: m.toolCalls.map((tc, i) =>
                    i === lastPlanIdx && tc.toolName === "create_plan"
                      ? { ...tc, items: nextItems }
                      : tc,
                  ),
                };
              });
              return;
            }
            if (evt.toolName === "jsx_preview") {
              const { title, html } = evt.args;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "jsx_preview",
                    toolCallId: evt.toolCallId,
                    title,
                    html,
                    status: "ok",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "execute_office_js_code") {
              const code = evt.args.code;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "execute_office_js_code",
                    toolCallId: evt.toolCallId,
                    code,
                    status: "running",
                  },
                ],
              }));
              void runInSandbox(code, { signal: bridgeAbort.signal }).then(
                (result) => {
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId && tc.toolName === "execute_office_js_code"
                        ? { ...tc, status: result.ok ? "ok" : "error", result }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, result);
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId && tc.toolName === "execute_office_js_code"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            if (evt.toolName === "code_interpreter") {
              const code = "code" in evt.args ? evt.args.code : undefined;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "code_interpreter",
                    toolCallId: evt.toolCallId,
                    code,
                    status: "running",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "file_search") {
              const queries = "queries" in evt.args ? evt.args.queries : undefined;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "file_search",
                    toolCallId: evt.toolCallId,
                    queries,
                    status: "running",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "web_search") {
              const queries = "queries" in evt.args ? evt.args.queries : undefined;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "web_search",
                    toolCallId: evt.toolCallId,
                    queries,
                    status: "running",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "use_skill") {
              // Server-only tool — already executed before the event fires.
              // Just surface it on the UI; no reply needed.
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "use_skill",
                    toolCallId: evt.toolCallId,
                    slug: evt.args.slug,
                    status: "ok",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "ask_user") {
              // Pause the run visually; the ToolCard renders an inline reply
              // affordance and calls `runs.toolResult` once the user answers.
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "ask_user",
                    toolCallId: evt.toolCallId,
                    question: evt.args.question,
                    ...(evt.args.options !== undefined
                      ? { options: evt.args.options }
                      : {}),
                    status: "running",
                  },
                ],
              }));
              return;
            }
            if (
              evt.toolName === "create_skill" ||
              evt.toolName === "edit_skill" ||
              evt.toolName === "delete_skill"
            ) {
              void queryClient.invalidateQueries({
                queryKey: [["skills", "list"]],
              });
            }
            if (
              evt.toolName === "create_skill" ||
              evt.toolName === "edit_skill"
            ) {
              const { slug, name, description } = evt.args;
              const toolName = evt.toolName;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName,
                    toolCallId: evt.toolCallId,
                    slug,
                    name,
                    description,
                    status: "ok",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "list_skills") {
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "list_skills",
                    toolCallId: evt.toolCallId,
                    status: "ok",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "delete_skill") {
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "delete_skill",
                    toolCallId: evt.toolCallId,
                    slug: evt.args.slug,
                    status: "ok",
                  },
                ],
              }));
              return;
            }
            if (evt.toolName === "peek_slide") {
              const { slideIndex, width, height } = evt.args;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "peek_slide",
                    toolCallId: evt.toolCallId,
                    slideIndex,
                    status: "running",
                  },
                ],
              }));
              void executeOps([{ op: "getSlideImage", slide: slideIndex, width, height }]).then(
                (results) => {
                  const raw = results[0];
                  if (typeof raw !== "object" || raw === null) {
                    throw new Error("getSlideImage returned non-object");
                  }
                  let base64 = "";
                  let mediaType = "";
                  for (const [k, v] of Object.entries(raw)) {
                    if (k === "base64" && typeof v === "string") base64 = v;
                    if (k === "mediaType" && typeof v === "string") mediaType = v;
                  }
                  if (!base64 || !mediaType) throw new Error("getSlideImage missing fields");
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId && tc.toolName === "peek_slide"
                        ? { ...tc, status: "ok", base64, mediaType }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, { base64, mediaType, slideIndex });
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId && tc.toolName === "peek_slide"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            if (evt.toolName === "insert_sandbox_image") {
              const { filename, slideIndex, rect, base64 } = evt.args;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "insert_sandbox_image",
                    toolCallId: evt.toolCallId,
                    filename,
                    slideIndex,
                    status: "running",
                  },
                ],
              }));
              void executeOps([
                { op: "addPicture", slide: slideIndex, base64, rect },
              ]).then(
                (results) => {
                  const raw = results[0];
                  if (typeof raw !== "object" || raw === null) {
                    throw new Error("addPicture returned non-object");
                  }
                  let resolvedSlideIndex = -1;
                  let shapeId = "";
                  for (const [k, v] of Object.entries(raw)) {
                    if (k === "slideIndex" && typeof v === "number") resolvedSlideIndex = v;
                    if (k === "shapeId" && typeof v === "string") shapeId = v;
                  }
                  if (resolvedSlideIndex < 0 || !shapeId) {
                    throw new Error("addPicture missing fields");
                  }
                  const result = { slideIndex: resolvedSlideIndex, shapeId };
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "insert_sandbox_image"
                        ? { ...tc, status: "ok", result }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, result);
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "insert_sandbox_image"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            if (evt.toolName === "upload_current_deck_to_sandbox") {
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "upload_current_deck_to_sandbox",
                    toolCallId: evt.toolCallId,
                    status: "running",
                  },
                ],
              }));
              void (async () => {
                const bytes = await captureDeckBytes();
                const slides = await parseDeck(bytes);
                return {
                  base64: bytesToBase64(bytes),
                  slideCount: slides.length,
                };
              })().then(
                (payload) => {
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "upload_current_deck_to_sandbox"
                        ? {
                            ...tc,
                            status: "ok",
                            result: { slideCount: payload.slideCount },
                          }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, payload);
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "upload_current_deck_to_sandbox"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            if (evt.toolName === "insert_sandbox_deck_slides") {
              const { filename, base64, mappings } = evt.args;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "insert_sandbox_deck_slides",
                    toolCallId: evt.toolCallId,
                    filename,
                    status: "running",
                  },
                ],
              }));
              const scratchTag = `bigpp:scratch:${runId}`;
              void (async () => {
                if (mappings && mappings.length > 0) {
                  return applySlideMappings(base64, mappings);
                }
                return insertAllSlidesAsScratch(base64, scratchTag);
              })().then(
                (result) => {
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "insert_sandbox_deck_slides"
                        ? { ...tc, status: "ok", result }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, result);
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "insert_sandbox_deck_slides"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            if (evt.toolName === "insert_attachment_image") {
              const { attachmentId, slideIndex, rect, base64 } = evt.args;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "insert_attachment_image",
                    toolCallId: evt.toolCallId,
                    attachmentId,
                    slideIndex,
                    status: "running",
                  },
                ],
              }));
              void executeOps([
                { op: "addPicture", slide: slideIndex, base64, rect },
              ]).then(
                (results) => {
                  const raw = results[0];
                  if (typeof raw !== "object" || raw === null) {
                    throw new Error("addPicture returned non-object");
                  }
                  let resolvedSlideIndex = -1;
                  let shapeId = "";
                  for (const [k, v] of Object.entries(raw)) {
                    if (k === "slideIndex" && typeof v === "number") resolvedSlideIndex = v;
                    if (k === "shapeId" && typeof v === "string") shapeId = v;
                  }
                  if (resolvedSlideIndex < 0 || !shapeId) {
                    throw new Error("addPicture missing fields");
                  }
                  const result = { slideIndex: resolvedSlideIndex, shapeId };
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "insert_attachment_image"
                        ? { ...tc, status: "ok", result }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, result);
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId &&
                      tc.toolName === "insert_attachment_image"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            if (evt.toolName === "insert_deck_slides") {
              const { id: attachmentId, base64, mappings } = evt.args;
              updateAssistant(assistantId, (m) => ({
                ...m,
                toolCalls: [
                  ...m.toolCalls,
                  {
                    toolName: "insert_deck_slides",
                    toolCallId: evt.toolCallId,
                    attachmentId,
                    status: "running",
                  },
                ],
              }));
              const scratchTag = `bigpp:scratch:${runId}`;
              void (async () => {
                if (mappings && mappings.length > 0) {
                  return applySlideMappings(base64, mappings);
                }
                return insertAllSlidesAsScratch(base64, scratchTag);
              })().then(
                (result) => {
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId && tc.toolName === "insert_deck_slides"
                        ? { ...tc, status: "ok", result }
                        : tc,
                    ),
                  }));
                  replyOk(evt.toolCallId, result);
                },
                (err: unknown) => {
                  const message = err instanceof Error ? err.message : String(err);
                  updateAssistant(assistantId, (m) => ({
                    ...m,
                    toolCalls: m.toolCalls.map((tc) =>
                      tc.toolCallId === evt.toolCallId && tc.toolName === "insert_deck_slides"
                        ? { ...tc, status: "error", error: message }
                        : tc,
                    ),
                  }));
                  replyErr(evt.toolCallId, message);
                },
              );
              return;
            }
            return;
          }
          case "tool-call-result-ack":
            // Flip built-in tool cards (which have no client-side dispatch)
            // from "running" to "ok" when the server acks the result.
            updateAssistant(assistantId, (m) => ({
              ...m,
              toolCalls: m.toolCalls.map((tc) =>
                tc.toolCallId === evt.toolCallId &&
                (tc.toolName === "code_interpreter" ||
                  tc.toolName === "file_search" ||
                  tc.toolName === "web_search") &&
                tc.status === "running"
                  ? { ...tc, status: "ok" }
                  : tc,
              ),
            }));
            return;
          case "step-finish":
            return;
          case "run-finish":
            updateAssistant(assistantId, (m) => ({
              ...m,
              finished: true,
              finishedAt: m.finishedAt ?? Date.now(),
              toolCalls: finishRunningToolCalls(m.toolCalls),
            }));
            // Preserve a prior terminal error (error event arrives before
            // run-finish). Overwriting to idle hid the Alert.
            setStatus((prev) => (prev.kind === "error" ? prev : { kind: "idle" }));
            if (activeRunIdRef.current === runId) {
              activeRunIdRef.current = null;
              activeAssistantIdRef.current = null;
            }
            persistAssistant(assistantId);
            return;
          case "cancelled":
            updateAssistant(assistantId, (m) => {
              if (m.finished) return m;
              return {
                ...m,
                content: m.content + (m.content.length > 0 ? "\n\n" : "") + "[cancelled]",
                finished: true,
                finishedAt: Date.now(),
                toolCalls: finishRunningToolCalls(m.toolCalls, "Cancelled"),
              };
            });
            setStatus((prev) => (prev.kind === "error" ? prev : { kind: "idle" }));
            if (activeRunIdRef.current === runId) {
              activeRunIdRef.current = null;
              activeAssistantIdRef.current = null;
            }
            persistAssistant(assistantId);
            return;
          case "error":
            updateAssistant(assistantId, (m) => {
              if (m.finished && m.content.includes(`Error: ${evt.message}`)) return m;
              return {
                ...m,
                content:
                  m.content + (m.content.length > 0 ? "\n\n" : "") + `Error: ${evt.message}`,
                finished: true,
                finishedAt: Date.now(),
                toolCalls: finishRunningToolCalls(m.toolCalls, evt.message),
              };
            });
            setStatus({ kind: "error", message: evt.message });
            if (activeRunIdRef.current === runId) {
              activeRunIdRef.current = null;
              activeAssistantIdRef.current = null;
            }
            persistAssistant(assistantId);
            return;
        }
      };

      const streamStartedAt = Date.now();
      let streamStartLogged = false;
      const threadIdAtStart = activeThreadIdRef.current;
      const sub = trpcClient.runs.stream.subscribe(
        { runId },
        {
          onData: (data) => {
            if (!streamStartLogged) {
              streamStartLogged = true;
              log.info("addin.agent.stream.start", { threadId: threadIdAtStart });
            }
            if (data.type === "run-finish") {
              log.info("addin.agent.stream.end", {
                threadId: threadIdAtStart,
                durationMs: Date.now() - streamStartedAt,
              });
            }
            handleEvent(data);
          },
          onError: (err) => {
            const message = err.message;
            log.error("addin.agent.error", { threadId: threadIdAtStart, error: message });
            updateAssistant(assistantId, (m) => {
              if (m.finished) return m;
              return {
                ...m,
                content:
                  m.content +
                  (m.content.length > 0 ? "\n\n" : "") +
                  `Stream error: ${message}`,
                finished: true,
                finishedAt: Date.now(),
                toolCalls: finishRunningToolCalls(m.toolCalls, message),
              };
            });
            setStatus({ kind: "error", message });
            if (activeRunIdRef.current === runId) {
              activeRunIdRef.current = null;
              activeAssistantIdRef.current = null;
            }
          },
          onComplete: () => {
            if (activeRunIdRef.current !== runId) return;
            activeRunIdRef.current = null;
            activeAssistantIdRef.current = null;
            setStatus((prev) => (prev.kind === "error" ? prev : { kind: "idle" }));
            updateAssistant(assistantId, (m) => {
              if (m.finished) return m;
              return {
                ...m,
                finished: true,
                finishedAt: m.finishedAt ?? Date.now(),
                toolCalls: finishRunningToolCalls(m.toolCalls),
              };
            });
          },
        },
      );
      subUnsubRef.current = () => {
        sub.unsubscribe();
      };
    },
    [messages, pinnedSkillIds, start, sendToolResult, upload, status.kind, updateAssistant],
  );

  return {
    messages,
    status,
    send,
    stop,
    newChat,
    answerAskUser,
    pinnedSkillIds,
    setPinnedSkillIds,
    activeThreadId,
    loadThread,
  };
};

